/**
 * Shaping for the "who got a statement, and what's the oldest invoice on it"
 * report offered once a bulk statement send finishes.
 *
 * Pure data only — the rows arrive already derived on the server (see
 * `famousStatementSendSummary.ts`, the Famous-specific seam), so this file
 * never has to know how "oldest" was decided. Swapping that seam changes the
 * numbers, not this code.
 */

import type { ArStatementSendSummary } from '@erp/rpc';
import { neutralizeSpreadsheetFormula } from '@/components/common/chat/tableToolResultData';
import { formatDate } from '../ar-format';

/** Column headers, in order. Shared by the CSV download and the clipboard copy. */
export const BULK_STATEMENT_SUMMARY_HEADERS = [
  'Customer',
  'Oldest Invoice #',
  'Oldest Invoice Date',
  'Oldest Invoice Balance',
] as const;

const EMPTY_CELL = '';

function cells(summary: ArStatementSendSummary): string[] {
  return [
    summary.customerErpDisplayName,
    summary.oldestInvoiceNumber ?? EMPTY_CELL,
    // Formatted for humans; the raw ISO date is not more useful in a report
    // that gets pasted into an email as often as into a spreadsheet.
    summary.oldestInvoiceDate
      ? formatDate(summary.oldestInvoiceDate)
      : EMPTY_CELL,
    // Left as a bare decimal string (no currency symbol or thousands
    // separators) so spreadsheets read it as a number.
    summary.oldestInvoiceBalanceDue ?? EMPTY_CELL,
  ].map(neutralizeSpreadsheetFormula);
}

/**
 * Label-keyed records for `downloadCSV`, which writes one column per key.
 */
export function buildBulkStatementSummaryRecords(
  summaries: ArStatementSendSummary[],
): Array<Record<string, string>> {
  return summaries.map((summary) =>
    Object.fromEntries(
      cells(summary).map((cell, index) => [
        BULK_STATEMENT_SUMMARY_HEADERS[index],
        cell,
      ]),
    ),
  );
}

/**
 * Tab-separated text for the clipboard — pastes into Excel/Sheets as columns
 * and stays readable in an email. Tabs and newlines inside a value would split
 * a row, so they collapse to spaces.
 */
export function buildBulkStatementSummaryTsv(
  summaries: ArStatementSendSummary[],
): string {
  const rows = [
    [...BULK_STATEMENT_SUMMARY_HEADERS],
    ...summaries.map((summary) => cells(summary)),
  ];
  return rows
    .map((row) => row.map((cell) => cell.replace(/[\t\r\n]+/g, ' ')).join('\t'))
    .join('\n');
}
